import * as Yup from 'yup';
import Moment from 'moment/moment';
import {
  noOfEmployees,
  name,
  revenue,
} from '../../../utils/validationSchemas/accounts.schema';
import { useCowbellTranslations } from '../../../i18n/translations';

export const useAccountValidationSchema = () => {
  const { t } = useCowbellTranslations();
  return Yup.object().shape({
    name,
    revenue,
    noOfEmployeesAll: noOfEmployees,
    yearEstablished: Yup.number()
      .label('Year Established')
      .typeError('Year Established is required')
      .lessThan(
        Moment().add(1, 'y').year(),
        'Year Established must be less than or equal to current year'
      )
      .moreThan(1499, 'Year Established must be greater than or equal to 1500')
      .required(),
    ownershipType: Yup.string()
      .label(t('account:ownershipType.label'))
      .required(),
    naicsCode: Yup.number()
      .label('Industry Code')
      .transform((current, original) =>
        typeof original === 'object' && original !== null
          ? Number(original.value)
          : Number(original)
      )
      .min(10, 'Industry Code must be atleast 2 digits')
      .max(999999, 'Industry Code be less than 6 digits')
      .nullable()
      .required(),
    secondNaicsCode: Yup.number()
      .label('Secondary Industry Code')
      .when('primaryNaicsPercentage', {
        is: (value) => Number(value) === 100,
        then: Yup.number()
          .typeError(
            'Secondary Industry Code is not allowed when NiacsCode is 100%'
          )
          .nullable()
          .test({
            name: 'secondNaicsCode',
            message:
              'Secondary Industry Code is not allowed when Industry code is 100%',
            test: (value) => !value,
          }),
        otherwise: Yup.number()
          .transform((current, original) =>
            typeof original === 'object' && original !== null
              ? Number(original.value)
              : Number(original)
          )
          .min(10, 'Secondary Industry Code must be atleast 2 digits')
          .max(999999, 'Secondary Industry Code must be less than 6 digits')
          .nullable()
          .required(),
      }),
    primaryNaicsPercentage: Yup.number()
      .label('Industry Code percentage')
      .min(51, 'Primary Industry Percentage must be 51% or higher')
      .max(100, 'Primary Industry Percentage must be 100% or lower')
      .required(),
    address1: Yup.string().label('Address').required(),
    city: Yup.string()
      .label('City')
      .required()
      .matches(/^[a-zA-Z\s-]+$/, 'Invalid City'),
    state: Yup.string().label('State').required(),
    zipCode: Yup.string()
      .label('Zip Code')
      .matches(/^\d{5}(-\d{4})?(?!-)$/, 'Invalid ZIP Code')
      .test('Is ZIP 00000', 'Invalid ZIP Code', (value) => value != '00000')
      .required(),
    phoneNumber: Yup.number()
      .label('Business Phone')
      .fromPhone()
      .phone()
      .required(),
    entityType: Yup.string().required().ensure().label('Entity Type'),
    isFranchise: Yup.bool().required().label('Franchise'),
  });
};
